import { useEffect, useMemo, useState } from 'react'
import { FileText, ImageOff, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  getOrderItemMediaUrlFallback,
  isOrderItemImageMedia,
  orderItemMediaFileLabel,
  resolveOrderItemMediaUrl,
} from '@/lib/order-item-media'
import type { StoreOrderItemMedia } from '@/types'

type OrderItemMediaPreviewProps = {
  media: StoreOrderItemMedia
  className?: string
  imageClassName?: string
  showLabel?: boolean
  onRemove?: () => void
  removeDisabled?: boolean
  removeLabel?: string
}

export function OrderItemMediaPreview({
  media,
  className,
  imageClassName,
  showLabel = true,
  onRemove,
  removeDisabled,
  removeLabel = 'Remove',
}: OrderItemMediaPreviewProps) {
  const isImage = isOrderItemImageMedia(media)
  const primaryUrl = useMemo(
    () => resolveOrderItemMediaUrl(media.path) ?? String(media.path ?? ''),
    [media.path],
  )
  const fallbackUrl = useMemo(
    () => getOrderItemMediaUrlFallback(media.path),
    [media.path],
  )

  const [imageSrc, setImageSrc] = useState(primaryUrl)
  const [imageFailed, setImageFailed] = useState(false)

  useEffect(() => {
    setImageSrc(primaryUrl)
    setImageFailed(false)
  }, [primaryUrl])

  const label = orderItemMediaFileLabel(media)
  const openUrl = imageFailed ? (fallbackUrl ?? primaryUrl) : (imageSrc || primaryUrl)

  const previewBody = isImage ? (
    imageFailed || !imageSrc ? (
      <div className="flex min-h-40 flex-col items-center justify-center gap-2 bg-muted/40 px-4 py-6 text-muted-foreground">
        <ImageOff className="h-8 w-8" />
        <span className="text-center text-xs">Preview unavailable</span>
      </div>
    ) : (
      <img
        src={imageSrc}
        alt={label}
        className={imageClassName ?? 'h-44 w-full object-contain bg-muted/30 p-2'}
        loading="lazy"
        onError={() => {
          if (fallbackUrl && imageSrc !== fallbackUrl) {
            setImageSrc(fallbackUrl)
            return
          }

          setImageFailed(true)
        }}
      />
    )
  ) : (
    <div className="flex min-h-32 flex-col items-center justify-center gap-2 bg-muted/30 px-4 py-6 text-muted-foreground">
      <FileText className="h-8 w-8" />
      <span className="text-center text-xs">{media.mime ?? media.type_str ?? 'Document'}</span>
    </div>
  )

  return (
    <div className={className ?? 'group relative overflow-hidden rounded-lg border border-border/70 bg-background'}>
      {onRemove ? (
        <Button
          type="button"
          size="icon"
          variant="destructive"
          className="absolute right-2 top-2 z-10 h-7 w-7 rounded-full opacity-90 shadow-lg transition-opacity group-hover:opacity-100"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onRemove()
          }}
          disabled={removeDisabled}
          aria-label={removeLabel || 'Remove attachment'}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ) : null}
      <a
        href={openUrl || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        {previewBody}
      </a>
      {showLabel ? (
        <div className="border-t border-border/60 px-2 py-1.5">
          <a
            href={openUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="block min-w-0 truncate text-xs text-muted-foreground hover:text-foreground"
            title={label}
          >
            {label}
          </a>
        </div>
      ) : null}
    </div>
  )
}

type LocalFileMediaPreviewProps = {
  file: File
  className?: string
  onRemove?: () => void
}

export function LocalFileMediaPreview({ file, className, onRemove }: LocalFileMediaPreviewProps) {
  const previewUrl = useMemo(() => URL.createObjectURL(file), [file])

  useEffect(() => () => {
    URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const isImage = file.type.startsWith('image/')

  return (
    <div className={className ?? 'overflow-hidden rounded-lg border border-dashed border-primary/40 bg-primary/5'}>
      {isImage ? (
        <img
          src={previewUrl}
          alt={file.name}
          className="h-44 w-full object-contain bg-muted/20 p-2"
        />
      ) : (
        <div className="flex min-h-32 flex-col items-center justify-center gap-2 px-4 py-6 text-muted-foreground">
          <FileText className="h-8 w-8" />
          <span className="text-center text-xs">{file.type || 'File'}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 border-t border-border/60 px-2 py-1.5">
        <span className="min-w-0 truncate text-xs text-muted-foreground" title={file.name}>
          {file.name}
        </span>
        {onRemove ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 shrink-0 px-2 text-destructive hover:text-destructive"
            onClick={onRemove}
          >
            Remove
          </Button>
        ) : null}
      </div>
    </div>
  )
}
